import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, InputNumber, Radio, Space, Typography, Table, Tag, message, Modal, Spin, Divider, Row, Col, Statistic, Input, Upload, Image, Form, Select, Popconfirm } from 'antd';
import { WechatOutlined, AlipayOutlined, WalletOutlined, GiftOutlined, ReloadOutlined, CopyOutlined, ClockCircleOutlined, UploadOutlined, InboxOutlined, UnorderedListOutlined, CrownOutlined, DeleteOutlined } from '@ant-design/icons';
import { rechargeAPI, inviteAPI, withdrawAPI, authAPI, balanceAPI, renewalAPI } from '../services/api';
import { copyToClipboard as clipboardCopy } from '../utils/clipboard';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const POINTS_RATIO = 100;

const { Option } = Select;

const { Title, Text } = Typography;

interface RechargeOrder {
  orderId: string;
  amount: string;
  paymentMethod: 'wechat' | 'alipay';
  status: string;
  paidAt?: string;
  createdAt: string;
}

interface InviteCode {
  id: number;
  code: string;
  is_used: boolean;
  expires_at: string;
  created_at: string;
  purchase_price?: number;
  used_by_username?: string | null;
  rebateAmount?: number;
  refunded?: boolean;
  renewal_unit?: string;
  renewal_quantity?: number;
}

interface WithdrawRequest {
  id: number;
  amount: number;
  application_code?: string;
  status: 'pending' | 'approved' | 'rejected';
  receipt_image?: string;
  note?: string | null;
  admin_note?: string | null;
  processed_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface PaymentConfig {
  wechatEnabled: boolean;
  alipayEnabled: boolean;
  balancePaymentEnabled?: boolean;
  qrCodeTimeout?: number; // 二维码倒计时时间（秒）
  autoCloseDelay?: number; // 倒计时结束后自动关闭延迟时间（秒）
}

interface Product {
  id: number;
  productName: string;
  price: number;
  discountRate: number;
  rebateAmount: number;
  renewalUnit: 'day' | 'month' | 'year';
  renewalQuantity: number;
  isDefault: boolean;
  rebatePoints?: number;
}

const InviteManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [balance, setBalance] = useState<number>(0);
  const [exclusiveBalance, setExclusiveBalance] = useState<number>(0);
  const [balanceHistoryVisible, setBalanceHistoryVisible] = useState(false);
  const [balanceHistory, setBalanceHistory] = useState<any[]>([]);
  const [balanceHistoryLoading, setBalanceHistoryLoading] = useState(false);
  const [balanceHistoryPagination, setBalanceHistoryPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState<number>(100);
  const [paymentMethod, setPaymentMethod] = useState<'wechat' | 'alipay'>('wechat');
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>({ wechatEnabled: false, alipayEnabled: false, qrCodeTimeout: 300, autoCloseDelay: 3 });
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<{ qrCode: string; orderId: string; amount: string } | null>(null);
  const [countdown, setCountdown] = useState(300);
  const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
  const [priceInfo, setPriceInfo] = useState<{ originalPrice: number; discountRate: number; finalPrice: number; rebateAmount?: number } | null>(null);
  const [rebateAmount, setRebateAmount] = useState<number>(0); // 邀请返现金额
  const [myCodes, setMyCodes] = useState<InviteCode[]>([]);
  const [myCodesPagination, setMyCodesPagination] = useState({ current: 1, pageSize: 8 });
  const [withdrawRequests, setWithdrawRequests] = useState<WithdrawRequest[]>([]);
  const [withdrawRequestsPagination, setWithdrawRequestsPagination] = useState({ current: 1, pageSize: 5 });
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState<number>(0);
  const [withdrawNote, setWithdrawNote] = useState<string>('');
  const [withdrawImage, setWithdrawImage] = useState<string | null>(null);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
  const [purchaseConfig, setPurchaseConfig] = useState<{ products?: Product[]; wechatEnabled?: boolean; alipayEnabled?: boolean; balancePaymentEnabled?: boolean; qrCodeTimeout?: number; autoCloseDelay?: number } | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState<number>(1);
  const [purchasePaymentMethod, setPurchasePaymentMethod] = useState<'wechat' | 'alipay' | 'balance'>('balance');
  const [purchaseQrCodeModalVisible, setPurchaseQrCodeModalVisible] = useState(false);
  const [purchaseQrCodeData, setPurchaseQrCodeData] = useState<{ qrCode: string; orderId: string; amount: string } | null>(null);
  const [purchaseCountdown, setPurchaseCountdown] = useState(300);
  const [isPurchaseQrCodeExpired, setIsPurchaseQrCodeExpired] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const { updateUser, user } = useAuth();

  // 获取余额
  const fetchBalance = async () => {
    try {
      const response = await rechargeAPI.getBalance();
      if (response.data.status === 200) {
        setBalance(response.data.data.balance);
        if (response.data.data.exclusiveBalance !== undefined) {
          setExclusiveBalance(response.data.data.exclusiveBalance);
        }
      }
    } catch (error: any) {
      message.error('获取余额失败');
    }
  };

  // 获取支付配置（用于充值）
  const fetchPaymentConfig = async () => {
    try {
      const response = await rechargeAPI.getConfig();
      if (response.data && response.data.status === 200 && response.data.data) {
        const config = response.data.data;
        const newConfig = {
          wechatEnabled: Boolean(config.wechatRechargeEnabled), // 使用充值开关
          alipayEnabled: Boolean(config.alipayRechargeEnabled), // 使用充值开关
          qrCodeTimeout: config.qrCodeTimeout || 300, // 从配置中读取二维码倒计时时间
          autoCloseDelay: config.autoCloseDelay || 3 // 从配置中读取自动关闭延迟时间
        };
        
        // 更新支付配置
        setPaymentConfig(newConfig);
        
        // 设置默认支付方式为第一个启用的支付方式
        if (newConfig.wechatEnabled) {
          setPaymentMethod('wechat');
        } else if (newConfig.alipayEnabled) {
          setPaymentMethod('alipay');
        } else {
          // 如果没有任何支付方式启用，重置支付方式
          setPaymentMethod('wechat'); // 设置一个默认值，即使不可用
        }
      } else {
        // 如果获取配置失败，使用默认配置（两个都禁用）
        setPaymentConfig({ wechatEnabled: false, alipayEnabled: false, qrCodeTimeout: 300, autoCloseDelay: 3 });
      }
    } catch (error: any) {
      // 获取配置失败时，使用默认配置
      setPaymentConfig({ wechatEnabled: false, alipayEnabled: false, qrCodeTimeout: 300, autoCloseDelay: 3 });
    }
  };

  // 获取卡密价格和返现金额
  const fetchPriceInfo = async () => {
    try {
      const response = await inviteAPI.getPrice();
      if (response.data.status === 200) {
        setPriceInfo(response.data.data);
      }
    } catch (error: any) {
      message.error('获取卡密价格失败');
    }
  };

  // 获取邀请返现统计
  const fetchBalanceHistory = async (page: number = 1, pageSize: number = balanceHistoryPagination.pageSize) => {
    setBalanceHistoryLoading(true);
    try {
      const response = await balanceAPI.getHistory(page, pageSize);
      if (response.data.status === 200) {
        setBalanceHistory(response.data.data.records);
        setBalanceHistoryPagination({
          current: response.data.data.page,
          pageSize: response.data.data.limit,
          total: response.data.data.total
        });
      }
    } catch (error: any) {
      console.error('获取余额变动记录失败:', error);
      message.error('获取余额变动记录失败');
    } finally {
      setBalanceHistoryLoading(false);
    }
  };

  const fetchRebateStats = async () => {
    try {
      const response = await inviteAPI.getRebateStats();
      if (response.data.status === 200) {
        const totalRebate = parseFloat(response.data.data.totalRebate || '0.00');
        setRebateAmount(totalRebate);
      }
    } catch (error: any) {
      console.error('获取邀请返现统计失败:', error);
      setRebateAmount(0);
    }
  };

  // 获取我的卡密列表
  const fetchMyCodes = async () => {
    try {
      const response = await inviteAPI.getMyCodes();
      if (response.data.status === 200) {
        setMyCodes(response.data.data);
      }
    } catch (error: any) {
      message.error('获取卡密列表失败');
    }
  };

  // 删除未使用的卡密
  const handleDeleteMyCode = async (codeId: number) => {
    try {
      const response = await inviteAPI.deleteMyCode(codeId);
      if (response.data.status === 200) {
        message.success(response.data.message);
        fetchMyCodes();
        fetchBalance();
        fetchRebateStats();
      } else {
        message.error(response.data.error || '删除失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const fetchWithdrawRequests = async () => {
    try {
      const response = await withdrawAPI.getMyRequests();
      if (response.data.status === 200) {
        setWithdrawRequests(response.data.data || []);
      }
    } catch (error: any) {
      message.error('获取提现记录失败');
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchPriceInfo();
    fetchMyCodes();
    fetchPaymentConfig();
    fetchWithdrawRequests();
    fetchRebateStats();
    fetchPurchaseConfig();
    renewalAPI.checkPaymentAvailable().then(res => {
      if (res.data.status === 200) {
        setHasPaymentMethod(res.data.data?.available || false);
      }
    }).catch(() => {});
  }, []);

  // 当充值弹窗打开时，重新获取支付配置
  useEffect(() => {
    if (rechargeModalVisible) {
      // 确保每次打开弹窗时都重新获取最新的支付配置
      fetchPaymentConfig();
    }
  }, [rechargeModalVisible]);

  // 充值倒计时
  useEffect(() => {
    if (qrCodeModalVisible && countdown > 0 && !isQrCodeExpired) {
      const timer = setTimeout(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsQrCodeExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [qrCodeModalVisible, countdown, isQrCodeExpired]);

  // 倒计时结束后自动关闭弹窗
  useEffect(() => {
    if (isQrCodeExpired && qrCodeData) {
      const autoCloseDelay = (paymentConfig.autoCloseDelay || 3) * 1000;
      const timeoutId = setTimeout(() => {
        const currentOrderId = qrCodeData.orderId;
        setQrCodeModalVisible(false);
        setQrCodeData(null);
        setIsQrCodeExpired(false);
        pollRechargeOrderStatus(currentOrderId);
      }, autoCloseDelay);
      return () => clearTimeout(timeoutId);
    }
  }, [isQrCodeExpired, qrCodeData, paymentConfig.autoCloseDelay]);

  // 轮询购买订单状态
  const pollPurchaseOrderStatus = useCallback(async (orderId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    let pollInterval: NodeJS.Timeout | null = null;

    const poll = async () => {
      attempts++;
      
      if (attempts >= maxAttempts) {
        message.warning('支付超时，请手动刷新页面查看订单状态');
        if (pollInterval) clearInterval(pollInterval);
        return;
      }

      try {
        const response = await inviteAPI.getPurchaseOrderStatus(orderId);
        
        if (response.data.status === 200) {
          const order = response.data.data;
          
          if (order.status === 'paid') {
            if (pollInterval) clearInterval(pollInterval);
            
            message.success('购买成功！');
            setPurchaseQrCodeModalVisible(false);
            setPurchaseQrCodeData(null);
            fetchBalance();
            fetchMyCodes();
            fetchRebateStats();
            if (updateUser) {
              await updateUser();
            }
            return;
          } else if (order.status === 'failed' || order.status === 'cancelled') {
            if (pollInterval) clearInterval(pollInterval);
            setPurchaseQrCodeModalVisible(false);
            setPurchaseQrCodeData(null);
            message.error('支付失败或已取消');
            return;
          }
        }

        if (pollInterval) clearInterval(pollInterval);
        pollInterval = setTimeout(poll, 5000);
      } catch (error: any) {
        console.error('查询订单状态错误:', error);
        // 即使出错也继续轮询，可能是网络问题
        if (pollInterval) clearInterval(pollInterval);
        pollInterval = setTimeout(poll, 5000);
      }
    };

    poll();
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [updateUser]);

  // 购买卡密倒计时
  useEffect(() => {
    if (purchaseQrCodeModalVisible && purchaseCountdown > 0 && !isPurchaseQrCodeExpired) {
      const timer = setTimeout(() => {
        setPurchaseCountdown((prev) => {
          if (prev <= 1) {
            setIsPurchaseQrCodeExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [purchaseQrCodeModalVisible, purchaseCountdown, isPurchaseQrCodeExpired]);

  // 购买卡密倒计时结束后自动关闭弹窗
  useEffect(() => {
    if (isPurchaseQrCodeExpired && purchaseQrCodeData) {
      const autoCloseDelay = (purchaseConfig?.autoCloseDelay || 3) * 1000;
      const timeoutId = setTimeout(() => {
        const currentOrderId = purchaseQrCodeData.orderId;
        setPurchaseQrCodeModalVisible(false);
        setPurchaseQrCodeData(null);
        setIsPurchaseQrCodeExpired(false);
        // 继续轮询订单状态
        pollPurchaseOrderStatus(currentOrderId);
      }, autoCloseDelay);
      return () => clearTimeout(timeoutId);
    }
  }, [isPurchaseQrCodeExpired, purchaseQrCodeData, purchaseConfig?.autoCloseDelay, pollPurchaseOrderStatus]);

  // 创建充值订单
  const handleRecharge = async () => {
    if (!rechargeAmount || rechargeAmount <= 0) {
      message.error('请输入有效的充值金额');
      return;
    }

    // 检查是否有可用的支付方式
    if (!paymentConfig.wechatEnabled && !paymentConfig.alipayEnabled) {
      message.error('当前没有可用的支付方式，请联系管理员配置');
      return;
    }

    // 检查当前选择的支付方式是否可用
    if (paymentMethod === 'wechat' && !paymentConfig.wechatEnabled) {
      message.error('微信支付未启用，请选择其他支付方式');
      return;
    }
    if (paymentMethod === 'alipay' && !paymentConfig.alipayEnabled) {
      message.error('支付宝未启用，请选择其他支付方式');
      return;
    }

    setLoading(true);
    try {
      const response = await rechargeAPI.createOrder({
        amount: rechargeAmount,
        paymentMethod
      });

      if (response.data.status === 200) {
        const { orderId, qrCode, amount } = response.data.data;
        if (qrCode) {
          setQrCodeData({ qrCode, orderId, amount });
          setQrCodeModalVisible(true);
          setCountdown(paymentConfig.qrCodeTimeout || 300); // 使用配置的倒计时时间
          setIsQrCodeExpired(false);
          setRechargeModalVisible(false);
          
          // 开始轮询订单状态
          pollRechargeOrderStatus(orderId);
        } else {
          message.warning('支付二维码生成失败，请稍后重试');
        }
      } else {
        message.error(response.data.error || '创建充值订单失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '创建充值订单失败');
    } finally {
      setLoading(false);
    }
  };

  // 轮询充值订单状态
  const pollRechargeOrderStatus = async (orderId: string) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        return;
      }

      try {
        const response = await rechargeAPI.getOrderStatus(orderId);
        if (response.data.status === 200) {
          const order = response.data.data;
          if (order.status === 'paid') {
            message.success('充值成功！');
            setQrCodeModalVisible(false);
            fetchBalance();
            if (updateUser) {
              await updateUser();
            }
            return;
          }
        }
      } catch (error) {
        // 忽略轮询错误
      }

      attempts++;
      setTimeout(poll, 5000); // 每5秒轮询一次
    };

    poll();
  };

  // 获取购买卡密配置（商品列表和支付方式）
  const fetchPurchaseConfig = async () => {
    try {
      const response = await authAPI.getRenewalConfig();
      if (response.data.status === 200 && response.data.data) {
        const cfg = response.data.data;
        setPurchaseConfig({
          products: cfg.products,
          wechatEnabled: cfg.wechatEnabled,
          alipayEnabled: cfg.alipayEnabled,
          balancePaymentEnabled: cfg.balancePaymentEnabled,
          qrCodeTimeout: cfg.qrCodeTimeout,
          autoCloseDelay: cfg.autoCloseDelay,
        });

        let defaultProduct: Product | null = null;
        if (cfg.products && cfg.products.length > 0) {
          defaultProduct = cfg.products.find((p: Product) => p.isDefault) || cfg.products[0];
          if (defaultProduct) {
            setSelectedProductId(defaultProduct.id);
            setSelectedProduct(defaultProduct);
          }
        }

        const defaultPaymentMethod = cfg.balancePaymentEnabled ? 'balance' :
                                    cfg.wechatEnabled ? 'wechat' :
                                    cfg.alipayEnabled ? 'alipay' : 'balance';
        
        form.setFieldsValue({
          productId: defaultProduct?.id,
          quantity: 1,
          paymentMethod: defaultPaymentMethod,
        });
        setPurchaseQuantity(1);
        setPurchasePaymentMethod(defaultPaymentMethod);
      } else {
        // 静默处理，不弹出错误提示
        setPurchaseConfig(null);
      }
    } catch (error: any) {
      // 静默处理，不弹出错误提示
      console.error('获取购买卡密配置错误:', error);
      setPurchaseConfig(null);
    }
  };

  // 打开充值弹窗
  const handleOpenRechargeModal = () => {
    if (!paymentConfig.wechatEnabled && !paymentConfig.alipayEnabled) {
      message.warning('充值功能暂未开启');
      return;
    }
    setRechargeModalVisible(true);
  };

  const handleRenewal = () => {
    window.dispatchEvent(new CustomEvent('show-renewal-modal'));
  };

  // 打开提现弹窗
  const handleOpenWithdrawModal = () => {
    // 检查余额是否为0
    if (balance <= 0) {
      message.warning('余额为0，不可提现');
      return;
    }
    setWithdrawModalVisible(true);
    setWithdrawAmount(0);
  };

  // 打开购买卡密弹窗
  const handleOpenPurchaseModal = () => {
    // 检查是否有可用的支付方式
    if (!purchaseConfig || (!purchaseConfig.wechatEnabled && !purchaseConfig.alipayEnabled && !purchaseConfig.balancePaymentEnabled)) {
      message.warning('购买卡密暂未开放');
      return;
    }
    setPurchaseModalVisible(true);
    fetchPurchaseConfig();
  };

  // 计算购买价格
  const calculatePurchasePrice = () => {
    if (!selectedProduct) return '0.00';
    const pricePerUnit = selectedProduct.price;
    const discountRate = selectedProduct.discountRate || 1.00;
    const discountedPricePerUnit = pricePerUnit * discountRate;
    const totalPrice = discountedPricePerUnit * purchaseQuantity;
    return totalPrice.toFixed(2);
  };

  // 购买卡密
  const handlePurchaseCode = async (values: { productId: number; quantity: number; paymentMethod: 'wechat' | 'alipay' | 'balance' }) => {
    if (!values.productId) {
      message.error('请选择商品');
      return;
    }

    if (!values.quantity || values.quantity < 1) {
      message.error('请选择数量');
      return;
    }

    setLoading(true);
    try {
      const response = await inviteAPI.purchaseCode({
        productId: values.productId,
        quantity: values.quantity,
        paymentMethod: values.paymentMethod
      });
      
      if (response.data.status === 200) {
        const { orderId, paymentUrl, qrCode, amount, paymentMethod: responsePaymentMethod } = response.data.data;
        
        // 如果是余额支付，直接成功
        if (responsePaymentMethod === 'balance' || values.paymentMethod === 'balance') {
          message.success('购买成功！');
          setPurchaseModalVisible(false);
          fetchBalance();
          fetchMyCodes();
          fetchRebateStats();
          if (updateUser) {
            await updateUser();
          }
          return;
        }
        
        // 如果是微信或支付宝支付，显示二维码
        if (qrCode) {
          setPurchaseQrCodeData({ qrCode, orderId, amount });
          setPurchaseCountdown(purchaseConfig?.qrCodeTimeout || 300);
          setIsPurchaseQrCodeExpired(false);
          setPurchaseModalVisible(false);
          setPurchaseQrCodeModalVisible(true);
          // 开始轮询订单状态
          pollPurchaseOrderStatus(orderId);
        } else if (paymentUrl) {
          // 打开支付页面
          const paymentWindow = window.open(paymentUrl, '_blank');
          message.info('正在跳转到支付页面，支付完成后请返回此页面查看结果');
          // 开始轮询订单状态（即使打开了新窗口，也要轮询）
          pollPurchaseOrderStatus(orderId);
          
          // 监听窗口关闭事件，如果用户关闭了支付窗口，继续轮询
          if (paymentWindow) {
            const checkClosed = setInterval(() => {
              if (paymentWindow.closed) {
                clearInterval(checkClosed);
                // 窗口已关闭，继续轮询直到支付完成或超时
              }
            }, 1000);
          }
        } else {
          message.error('支付链接生成失败，请稍后重试');
        }
      } else {
        message.error(response.data.error || '购买失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '购买失败');
    } finally {
      setLoading(false);
    }
  };

  const resetWithdrawForm = () => {
    setWithdrawAmount(0);
    setWithdrawNote('');
    setWithdrawImage(null);
  };

  const handleApplyWithdraw = async () => {
    if (!withdrawAmount || withdrawAmount <= 0) {
      message.error('请输入有效的提现金额');
      return;
    }
    if (withdrawAmount > balance) {
      message.error('提现金额不能超过当前余额');
      return;
    }
    if (!withdrawImage) {
      message.error('请上传收款码图片');
      return;
    }
    try {
      setWithdrawSubmitting(true);
      const response = await withdrawAPI.apply({
        amount: withdrawAmount,
        receiptImage: withdrawImage,
        note: withdrawNote,
      });
      if (response.data.status === 200) {
        message.success('提现申请已提交');
        setWithdrawModalVisible(false);
        resetWithdrawForm();
        fetchBalance();
        fetchWithdrawRequests();
      } else {
        message.error(response.data.error || '提现申请失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '提现申请失败');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const handleUploadReceipt = async (file: File) => {
    // 检查文件类型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return Upload.LIST_IGNORE;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB！');
      return Upload.LIST_IGNORE;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setWithdrawImage(reader.result as string);
    };
    reader.readAsDataURL(file);
    return false;
  };

  // 处理粘贴图片
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!withdrawModalVisible) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          if (blob) {
            const file = new File([blob], 'pasted-image.png', { type: blob.type });
            handleUploadReceipt(file);
            message.success('图片已粘贴');
          }
          break;
        }
      }
    };
    
    if (withdrawModalVisible) {
      document.addEventListener('paste', handlePaste);
      return () => {
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [withdrawModalVisible]);

  // 复制到剪贴板
  const copyToClipboard = async (text: string) => {
    const ok = await clipboardCopy(text);
    if (ok) message.success('复制成功');
    else message.error('复制失败，请手动复制');
  };

  const columns = [
    {
      title: '邀请码',
      dataIndex: 'code',
      key: 'code',
      width: 220,
      render: (text: string) => (
        <Space size="small">
          <Text code style={{ fontSize: 14 }}>{text}</Text>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(text)}
            style={{ padding: 0 }}
          >
            复制
          </Button>
        </Space>
      )
    },
    {
      title: '时长',
      key: 'duration',
      width: 90,
      render: (_: any, record: InviteCode) => {
        const unit = record.renewal_unit || 'month';
        const quantity = record.renewal_quantity || 1;
        const unitMap: Record<string, string> = {
          day: '天',
          month: '个月',
          year: '年',
          permanent: '永久'
        };
        if (unit === 'permanent') {
          return <Tag color="purple">永久</Tag>;
        }
        const label = quantity > 1 ? `${quantity}${unitMap[unit] || unit}` : unitMap[unit] || unit;
        return <Tag color="blue">{label}</Tag>;
      }
    },
    {
      title: '卡密状态',
      key: 'code_status',
      width: 100,
      render: (_: any, record: InviteCode) => {
        // 如果卡密已被使用，显示为已失效
        if (record.is_used) {
          return (
            <Tag color="default">
              已失效
            </Tag>
          );
        }
        
        // 如果卡密已过期，显示为已过期
        const isExpired = record.expires_at ? dayjs().isAfter(dayjs(record.expires_at)) : false;
        if (isExpired) {
          return (
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              已过期
            </Tag>
          );
        }
        
        // 否则显示为有效
        return (
          <Tag color="green">
            有效
          </Tag>
        );
      }
    },
    {
      title: '使用状态',
      key: 'usage_status',
      width: 110,
      render: (_: any, record: InviteCode) => {
        if (record.refunded) {
          return (
            <Tag color="default">
              已退款
            </Tag>
          );
        }
        if (record.is_used) {
          return (
            <Tag color="red">
              已使用
            </Tag>
          );
        }
        return (
          <Tag color="blue">
            未使用
          </Tag>
        );
      }
    },
    {
      title: '使用者',
      dataIndex: 'used_by_username',
      key: 'used_by_username',
      width: 120,
      render: (username: string | null | undefined) => username || '-'
    },
    {
      title: '邀请返现',
      key: 'rebate',
      width: 100,
      render: (_: any, record: InviteCode) => {
        if (record.is_used && record.rebateAmount && record.rebateAmount > 0) {
          return <Text type="success">¥{record.rebateAmount.toFixed(2)}</Text>;
        }
        return <Text type="secondary">-</Text>;
      }
    },
    {
      title: '购买价格',
      dataIndex: 'purchase_price',
      key: 'purchase_price',
      render: (price: number | string) => {
        if (!price) return '-';
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return isNaN(numPrice) ? '-' : `¥${numPrice.toFixed(2)}`;
      }
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (time: string) => {
        if (!time) return '-';
        // 数据库中存储的就是北京时间，直接格式化显示
        return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
      }
    },
    {
      title: '购买时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => {
        if (!time) return '-';
        // 数据库中存储的就是北京时间，直接格式化显示
        return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: InviteCode) => (
        !record.is_used ? (
          <Popconfirm
            title="确定要删除这个卡密吗？"
            description="删除后购买金额将返还到积分余额"
            onConfirm={() => handleDeleteMyCode(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0 }}
            >
              删除
            </Button>
          </Popconfirm>
        ) : null
      )
    }
  ];

  return (
    <div>
      <Card>
        <Title level={4}>邀请管理</Title>
        
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Statistic
                title={<span>充值积分 <span style={{ fontSize: 14, color: '#8c8c8c', fontWeight: 'normal' }}>≈ ¥{balance.toFixed(2)}</span></span>}
                value={balance * POINTS_RATIO}
                precision={0}
                valueStyle={{ color: '#3f8600' }}
                suffix={
                  <Space>
                    <Button
                      type="link"
                      icon={<UnorderedListOutlined />}
                      onClick={() => {
                        setBalanceHistoryVisible(true);
                        fetchBalanceHistory(1);
                      }}
                      size="small"
                    >
                      账户明细
                    </Button>
                    <Button
                      type="link"
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        fetchBalance();
                        fetchRebateStats();
                      }}
                      size="small"
                    >
                      刷新
                    </Button>
                  </Space>
                }
              />
              <div style={{ marginTop: 4 }}>
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>专享积分：</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#722ed1' }}>{(exclusiveBalance * POINTS_RATIO).toFixed(0)}</span>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>（仅接口消费）</span>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card style={{ height: '100%' }}>
              {purchaseConfig?.products && purchaseConfig.products.length > 0 ? (
                <div style={{ display: 'flex', gap: 16, width: '100%', height: '100%', alignItems: 'center' }}>
                  {purchaseConfig.products.map((product: Product) => {
                    const finalPrice = product.price * (product.discountRate || 1);
                    const discountRate = product.discountRate || 1;
                    const hasDiscount = discountRate < 1;
                    const discountPercent = hasDiscount ? ((1 - discountRate) * 100).toFixed(0) : '';
                    const hasRebate = product.rebateAmount != null && product.rebateAmount > 0;
                    const rebatePoints = product.rebatePoints || 0;
                    const hasRebatePoints = rebatePoints > 0;
                    return (
                      <div
                        key={product.id}
                        style={{
                          flex: 1,
                          minWidth: 120,
                          backgroundColor: '#fafafa',
                          borderRadius: 12,
                          padding: '18px 12px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                          position: 'relative',
                        }}
                      >
                        {hasDiscount && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              backgroundColor: '#ff4d4f',
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 600,
                              padding: '2px 10px',
                              borderRadius: 12,
                              transform: 'rotate(-12deg)',
                              transformOrigin: 'left center',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                              whiteSpace: 'nowrap',
                              zIndex: 2,
                            }}
                          >
                            优惠{discountPercent}%
                          </div>
                        )}
                        {hasRebate && (
                          <div
                            style={{
                              position: 'absolute',
                              top: hasDiscount ? 32 : 8,
                              left: 8,
                              backgroundColor: '#52c41a',
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 600,
                              padding: '2px 10px',
                              borderRadius: 12,
                              transform: 'rotate(-12deg)',
                              transformOrigin: 'left center',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            }}
                          >
                            返现{product.rebateAmount.toFixed(0)}元
                          </div>
                        )}
                        {hasRebatePoints && (
                          <div
                            style={{
                              position: 'absolute',
                              top: hasDiscount ? (hasRebate ? 56 : 32) : (hasRebate ? 32 : 8),
                              left: 8,
                              backgroundColor: '#722ed1',
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 600,
                              padding: '2px 10px',
                              borderRadius: 12,
                              transform: 'rotate(-12deg)',
                              transformOrigin: 'left center',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            }}
                          >
                            返{(rebatePoints * POINTS_RATIO).toFixed(0)}积分
                          </div>
                        )}
                        <div style={{ fontSize: 14, color: '#666' }}>{product.productName || ''}售价</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#cf1322', marginTop: 6 }}>
                          ¥ {finalPrice.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                (purchaseConfig?.wechatEnabled || purchaseConfig?.alipayEnabled || purchaseConfig?.balancePaymentEnabled) ? (
                <Statistic
                  value={priceInfo?.finalPrice || 0}
                  prefix="¥"
                  precision={2}
                  valueStyle={{ color: '#cf1322' }}
                />
                ) : (
                  <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                    <Text type="secondary" style={{ fontSize: '16px' }}>续费功能未启用</Text>
                  </div>
                )
              )}
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Statistic
                title="邀请返现"
                value={rebateAmount}
                prefix="¥"
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        <Space size="large" style={{ marginBottom: 24 }}>
          <Button 
            type="primary" 
            icon={<WalletOutlined />}
            onClick={handleOpenRechargeModal}
          >
            充值
          </Button>
          {hasPaymentMethod && (
            <Button 
              type="primary"
              icon={<CrownOutlined />}
              onClick={handleRenewal}
            >
              续费会员
            </Button>
          )}
          <Button 
            type="primary" 
            icon={<GiftOutlined />}
            onClick={handleOpenPurchaseModal}
            loading={loading}
          >
            购买卡密
          </Button>
          <Button 
            icon={<WalletOutlined />}
            onClick={handleOpenWithdrawModal}
          >
            提现
          </Button>
        </Space>

        <Divider />

        <Title level={5}>我的卡密 <Button type="link" icon={<ReloadOutlined />} onClick={fetchMyCodes} size="small" /></Title>
        <Table
          columns={columns}
          dataSource={myCodes}
          rowKey="id"
          pagination={{
            current: myCodesPagination.current,
            pageSize: myCodesPagination.pageSize,
            total: myCodes.length,
            onChange: (page, pageSize) => {
              setMyCodesPagination({ current: page, pageSize: pageSize || 8 });
            },
            onShowSizeChange: (current, size) => {
              setMyCodesPagination({ current: 1, pageSize: size });
            },
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['5', '8', '10', '20', '50'],
          }}
        />
      </Card>

      <Card style={{ marginTop: 24 }} title={<span>我的提现申请 <Button type="link" icon={<ReloadOutlined />} onClick={fetchWithdrawRequests} size="small" /></span>}>
        <Table
          dataSource={withdrawRequests}
          rowKey="id"
          pagination={{
            current: withdrawRequestsPagination.current,
            pageSize: withdrawRequestsPagination.pageSize,
            total: withdrawRequests.length,
            onChange: (page, pageSize) => {
              setWithdrawRequestsPagination({ current: page, pageSize: pageSize || 5 });
            },
            onShowSizeChange: (current, size) => {
              setWithdrawRequestsPagination({ current: 1, pageSize: size });
            },
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['5', '10', '20', '50'],
          }}
          columns={[
            {
              title: '申请码',
              dataIndex: 'application_code',
              render: (code: string | undefined) => code || '-',
            },
            {
              title: '申请时间',
              dataIndex: 'created_at',
              render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
            },
            {
              title: '金额',
              dataIndex: 'amount',
              render: (amount: number) => `¥${Number(amount || 0).toFixed(2)}`,
            },
            {
              title: '状态',
              dataIndex: 'status',
              render: (status: WithdrawRequest['status']) => {
                const map: Record<WithdrawRequest['status'], { color: string; text: string }> = {
                  pending: { color: 'orange', text: '审批中' },
                  approved: { color: 'green', text: '已完成' },
                  rejected: { color: 'red', text: '已拒绝' },
                };
                const cfg = map[status];
                return <Tag color={cfg.color}>{cfg.text}</Tag>;
              },
            },
            {
              title: '备注',
              dataIndex: 'note',
              render: (text: string | null) => text || '-',
            },
            {
              title: '管理员备注',
              dataIndex: 'admin_note',
              render: (text: string | null) => text || '-',
            },
            {
              title: '收款码',
              dataIndex: 'receipt_image',
              render: (img: string | undefined) =>
                img ? <Image src={img} width={60} style={{ borderRadius: 4 }} /> : '-',
            },
            {
              title: '处理时间',
              dataIndex: 'processed_at',
              render: (time: string | null) => (time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'),
            },
          ]}
        />
      </Card>

      {/* 充值弹窗 */}
      <Modal
        title="账户充值"
        open={rechargeModalVisible}
        onOk={handleRecharge}
        onCancel={() => setRechargeModalVisible(false)}
        okText="立即支付"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>充值金额：</Text>
            <InputNumber
              min={0.01}
              max={10000}
              step={10}
              value={rechargeAmount}
              onChange={(value) => setRechargeAmount(value || 10)}
              prefix="¥"
              style={{ width: '100%', marginTop: 8 }}
            />
          </div>
          <div>
            <Text strong>支付方式：</Text>
            {(!paymentConfig.wechatEnabled && !paymentConfig.alipayEnabled) ? (
              <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                <Text type="danger">当前没有可用的支付方式，请联系管理员配置</Text>
              </div>
            ) : (
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ marginTop: 8, width: '100%' }}
              >
                <Space size="large" style={{ width: '100%' }}>
                  {paymentConfig.wechatEnabled && (
                    <Radio value="wechat">
                      <Space>
                        <WechatOutlined style={{ fontSize: '16px', color: '#07c160' }} />
                        <Text>微信支付</Text>
                      </Space>
                    </Radio>
                  )}
                  {paymentConfig.alipayEnabled && (
                    <Radio value="alipay">
                      <Space>
                        <AlipayOutlined style={{ fontSize: '16px', color: '#1677ff' }} />
                        <Text>支付宝</Text>
                      </Space>
                    </Radio>
                  )}
                </Space>
              </Radio.Group>
            )}
          </div>
        </Space>
      </Modal>

      {/* 提现申请弹窗 */}
      <Modal
        title="提现申请"
        open={withdrawModalVisible}
        onCancel={() => {
          setWithdrawModalVisible(false);
          resetWithdrawForm();
        }}
        onOk={handleApplyWithdraw}
        confirmLoading={withdrawSubmitting}
        okText="提交申请"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>提现金额：</Text>
            <Space style={{ marginTop: 8 }}>
              <InputNumber
                min={0.01}
                precision={2}
                value={withdrawAmount}
                onChange={(value) => setWithdrawAmount(value || 0)}
                prefix="¥"
                style={{ width: 200 }}
              />
              <Button onClick={() => setWithdrawAmount(balance)}>全部</Button>
            </Space>
          </div>
          <div>
            <Text strong>收款码：</Text>
            <div style={{ marginTop: 8 }}>
              {!withdrawImage ? (
                <Upload.Dragger
                  accept="image/*"
                  beforeUpload={handleUploadReceipt}
                  showUploadList={false}
                  maxCount={1}
                  style={{
                    padding: '40px 20px',
                    border: '2px dashed #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <p className="ant-upload-drag-icon" style={{ marginBottom: 16 }}>
                    <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>
                    点击上传、拖拽或粘贴图片
                  </p>
                  <p className="ant-upload-hint" style={{ fontSize: 12, color: '#999' }}>
                    支持JPG、PNG、GIF等格式，Ctrl+V 快速粘贴
                  </p>
                </Upload.Dragger>
              ) : (
                <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Image 
                    src={withdrawImage} 
                    width={200} 
                    style={{ borderRadius: 4 }}
                    preview={false}
                  />
                  <Button
                    type="link"
                    danger
                    onClick={() => setWithdrawImage(null)}
                    style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
                  >
                    删除
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div>
            <Text strong>备注：</Text>
            <Input.TextArea
              rows={3}
              value={withdrawNote}
              onChange={(e) => setWithdrawNote(e.target.value)}
              placeholder="可填写到账信息、联系方式等"
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>

      {/* 支付二维码弹窗 */}
      <Modal
        title="扫码支付"
        open={qrCodeModalVisible}
        onCancel={() => setQrCodeModalVisible(false)}
        footer={null}
        width={400}
        centered
      >
        {qrCodeData && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeData.qrCode)}`}
                alt="支付二维码"
                style={{ 
                  width: '100%', 
                  maxWidth: 300, 
                  marginBottom: 16,
                  opacity: isQrCodeExpired ? 0.3 : 1,
                  transition: 'opacity 0.3s'
                }}
              />
              {isQrCodeExpired && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    maxWidth: 300,
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#fff',
                    zIndex: 10,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                    二维码已过期
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    {paymentConfig.autoCloseDelay ? `${paymentConfig.autoCloseDelay}秒后自动关闭` : '即将关闭'}
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>订单号：{qrCodeData.orderId}</Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>支付金额：¥{qrCodeData.amount}</Text>
            </div>
            <div>
              <Text type={countdown < 60 ? 'danger' : 'secondary'}>
                二维码有效期：{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 购买卡密弹窗 */}
      <Modal
        title="购买卡密"
        open={purchaseModalVisible}
        onCancel={() => setPurchaseModalVisible(false)}
        footer={null}
        width={600}
        centered
      >
        {purchaseConfig && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handlePurchaseCode}
            initialValues={{
              quantity: 1,
              paymentMethod: purchaseConfig.balancePaymentEnabled ? 'balance' : 
                            purchaseConfig.wechatEnabled ? 'wechat' : 
                            purchaseConfig.alipayEnabled ? 'alipay' : 'balance'
            }}
          >
            {/* 商品选择 */}
            {purchaseConfig.products && purchaseConfig.products.length > 0 && (
              <Form.Item
                label="选择商品"
                name="productId"
                rules={[{ required: true, message: '请选择商品' }]}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  {purchaseConfig.products.map((product: Product) => {
                    const isSelected = selectedProductId === product.id;
                    return (
                      <div
                        key={product.id}
                        onClick={() => {
                          setSelectedProductId(product.id);
                          setSelectedProduct(product);
                          form.setFieldsValue({ productId: product.id });
                        }}
                        style={{
                          border: isSelected ? '2px solid #1890ff' : '2px dashed #d9d9d9',
                          borderRadius: '4px',
                          padding: '16px 24px',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                          minWidth: '120px',
                          textAlign: 'center',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#1890ff';
                            e.currentTarget.style.backgroundColor = '#f0f9ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.backgroundColor = '#fff';
                          }
                        }}
                      >
                        <Text strong style={{ fontSize: '16px' }}>
                          {product.productName}(¥{product.price.toFixed(2)})
                        </Text>
                      </div>
                    );
                  })}
                </div>
                <input
                  type="hidden"
                  value={selectedProductId || ''}
                  onChange={() => {}}
                />
              </Form.Item>
            )}

            {/* 数量选择 */}
            {selectedProduct && (
              <Form.Item
                label="数量"
                name="quantity"
                rules={[
                  { required: true, message: '请输入数量' },
                  { type: 'number', min: 1, max: 100, message: '数量必须在1-100之间' },
                ]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                  addonAfter="个"
                  onChange={(value) => setPurchaseQuantity(value || 1)}
                />
              </Form.Item>
            )}

            {/* 选中商品的详细信息 */}
            {selectedProduct && (
              <div style={{ marginBottom: '16px', padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary">单价：</Text>
                  <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                    ¥{selectedProduct.price.toFixed(2)}
                  </Text>
                  <Text type="secondary" style={{ marginLeft: '16px' }}>数量：</Text>
                  <Text strong>{purchaseQuantity}个</Text>
                  {selectedProduct.rebateAmount > 0 && (
                    <>
                      <Text type="secondary" style={{ marginLeft: '16px' }}>邀请返现：</Text>
                      <Text strong style={{ color: '#ff4d4f' }}>
                        ¥{selectedProduct.rebateAmount.toFixed(2)}
                      </Text>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 支付方式 */}
            <Form.Item
              label="支付方式"
              name="paymentMethod"
              rules={[{ required: true, message: '请选择支付方式' }]}
            >
              <Radio.Group
                onChange={(e) => setPurchasePaymentMethod(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space size="large" style={{ width: '100%' }}>
                  {purchaseConfig.balancePaymentEnabled && (
                    <Radio value="balance">
                      <Space>
                        <WalletOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                        <Text>积分支付({(balance * POINTS_RATIO).toFixed(0)}积分 ≈ ¥{balance.toFixed(2)})</Text>
                      </Space>
                    </Radio>
                  )}
                  {purchaseConfig.wechatEnabled && (
                    <Radio value="wechat">
                      <Space>
                        <WechatOutlined style={{ fontSize: '16px', color: '#07c160' }} />
                        <Text>微信支付</Text>
                      </Space>
                    </Radio>
                  )}
                  {purchaseConfig.alipayEnabled && (
                    <Radio value="alipay">
                      <Space>
                        <AlipayOutlined style={{ fontSize: '16px', color: '#1677ff' }} />
                        <Text>支付宝</Text>
                      </Space>
                    </Radio>
                  )}
                </Space>
              </Radio.Group>
            </Form.Item>

            <Divider />

            <div style={{ marginBottom: '16px', textAlign: 'right' }}>
              {selectedProduct && (
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary">数量：</Text>
                  <Text strong style={{ fontSize: '16px' }}>{purchaseQuantity} 个</Text>
                </div>
              )}
              <Title level={4} style={{ margin: 0 }}>
                总计：¥{calculatePurchasePrice()}
              </Title>
            </div>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setPurchaseModalVisible(false)}>取消</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={purchasePaymentMethod === 'balance' && balance < parseFloat(calculatePurchasePrice())}
                >
                  {purchasePaymentMethod === 'balance' ? `积分支付(需${(parseFloat(calculatePurchasePrice()) * POINTS_RATIO).toFixed(0)}积分)` : '立即支付'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* 购买卡密支付二维码弹窗 */}
      <Modal
        title="扫码支付"
        open={purchaseQrCodeModalVisible}
        onCancel={() => {
          // 关闭弹窗时，如果订单还在轮询中，继续轮询
          if (purchaseQrCodeData) {
            pollPurchaseOrderStatus(purchaseQrCodeData.orderId);
          }
          setPurchaseQrCodeModalVisible(false);
        }}
        footer={null}
        width={400}
        centered
      >
        {purchaseQrCodeData && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(purchaseQrCodeData.qrCode)}`}
                alt="支付二维码"
                style={{ 
                  width: '100%', 
                  maxWidth: 300, 
                  marginBottom: 16,
                  opacity: isPurchaseQrCodeExpired ? 0.3 : 1,
                  transition: 'opacity 0.3s'
                }}
              />
              {isPurchaseQrCodeExpired && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    maxWidth: 300,
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#fff',
                    zIndex: 10,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                    二维码已过期
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    {purchaseConfig?.autoCloseDelay ? `${purchaseConfig.autoCloseDelay}秒后自动关闭` : '即将关闭'}
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>订单号：{purchaseQrCodeData.orderId}</Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>支付金额：¥{purchaseQrCodeData.amount}</Text>
            </div>
            <div>
              <Text type={purchaseCountdown < 60 ? 'danger' : 'secondary'}>
                二维码有效期：{Math.floor(purchaseCountdown / 60)}:{(purchaseCountdown % 60).toString().padStart(2, '0')}
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 账户明细弹窗 */}
      <Modal
        title="账户明细"
        open={balanceHistoryVisible}
        onCancel={() => setBalanceHistoryVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          dataSource={balanceHistory}
          loading={balanceHistoryLoading}
          rowKey="id"
          pagination={{
            current: balanceHistoryPagination.current,
            pageSize: balanceHistoryPagination.pageSize,
            total: balanceHistoryPagination.total,
            onChange: (page, pageSize) => {
              setBalanceHistoryPagination(prev => ({ ...prev, current: page, pageSize: pageSize || prev.pageSize }));
              fetchBalanceHistory(page, pageSize || balanceHistoryPagination.pageSize);
            },
            onShowSizeChange: (current, size) => {
              setBalanceHistoryPagination(prev => ({ ...prev, current: 1, pageSize: size }));
              fetchBalanceHistory(1, size);
            },
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            pageSizeOptions: ['5', '10', '20', '50', '100'],
          }}
          columns={[
            {
              title: '时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 180
            },
            {
              title: '类型',
              dataIndex: 'typeName',
              key: 'typeName',
              width: 100,
              render: (text: string, record: any) => {
                const colorMap: { [key: string]: string } = {
                  '充值': 'green',
                  '返现': 'green',
                  '退款': 'green',
                  '提现退回': 'green',
                  '管理员调整': 'blue',
                  '提现': 'red',
                  '购买': 'red',
                  '续费': 'red',
                  '接口消费': 'orange'
                };
                return <Tag color={colorMap[text] || 'default'}>{text}</Tag>;
              }
            },
            {
              title: '变动金额',
              dataIndex: 'amount',
              key: 'amount',
              width: 120,
              align: 'right' as const,
              render: (amount: number | undefined, record: any) => {
                const num = amount || 0;
                const exNum = typeof record.exclusiveAmount === 'number' ? record.exclusiveAmount : 0;
                if (exNum !== 0) {
                  return (
                    <span>
                      {num !== 0 && (
                        <span style={{ color: num > 0 ? '#3f8600' : '#cf1322', fontWeight: 'bold' }}>
                          {num > 0 ? '+' : ''}¥{num.toFixed(2)}{' '}
                        </span>
                      )}
                      <span style={{ color: exNum > 0 ? '#722ed1' : '#cf1322', fontWeight: 'bold' }}>
                        {exNum > 0 ? '+' : ''}{(exNum * POINTS_RATIO).toFixed(0)}积分(专享)
                      </span>
                    </span>
                  );
                }
                const isPositive = num > 0;
                return (
                  <span style={{ color: isPositive ? '#3f8600' : '#cf1322', fontWeight: 'bold' }}>
                    {isPositive ? '+' : ''}¥{num.toFixed(2)}
                  </span>
                );
              }
            },
            {
              title: '变动后余额',
              dataIndex: 'balanceAfter',
              key: 'balanceAfter',
              width: 120,
              align: 'right' as const,
              render: (balance: number | undefined) => `¥${Number(balance || 0).toFixed(2)}`
            },
            {
              title: '说明',
              dataIndex: 'description',
              key: 'description',
              ellipsis: true
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default InviteManagement;

